import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getData,
  deleteData,
  postData,
  putData,
} from "../../../utility/Utility";
import Swal from "sweetalert2";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";
import EmailModal from "../../../Common/Emailmodale/Emailmodale";
import PolicyDetailsModal from "../../../Modal/PolicyDetailsModal/PolicyDetailsModal";

function UserPolicyListbyAgent() {
  const navigate = useNavigate();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const rowsPerPage = 10;

  const fetchPolicies = async (
    page = 1,
    search = "",
    startDate = "",
    endDate = ""
  ) => {
    setLoading(true);
    try {
      const response = await postData("api/policies/all", {
        page: page,
        limit: rowsPerPage,
        search: search,
        startDate: startDate,
        endDate: endDate,
      });

      if (response?.success) {
        setPolicies(response.data);
        setTotalPages(Math.ceil(response.total / rowsPerPage));
        setTotalItems(response.total);
      } else {
        toast.error(response?.message || "Failed to fetch policies");
      }
    } catch (error) {
      toast.error("Error fetching policies");
      console.error("Fetch policies error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPolicies(currentPage + 1, searchTerm, startDate, endDate);
  }, [currentPage, searchTerm, startDate, endDate]);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchPolicies(1, searchTerm, startDate, endDate);
  };

  const handleReset = () => {
    setSearchTerm("");
    setCurrentPage(0);
    fetchPolicies(1, searchTerm, startDate, endDate);
  };

  const handleStatusUpdate = async (policyId, status, agent_id) => {
    let reason = undefined;
    let confirmed = false;
    if (status === "rejected") {
      const { value: rejectionReason, isConfirmed } = await Swal.fire({
        title: "Reject Inquiry",
        input: "textarea",
        inputLabel: "Please provide a reason for rejection",
        inputPlaceholder: "Type the reason here...",
        inputValue: "",
        inputAttributes: {
          autocapitalize: "off",
          required: true,
        },
        showCancelButton: true,
        confirmButtonText: "Reject",
        cancelButtonText: "Cancel",
        inputValidator: (value) => {
          if (!value) return "A reason is required!";
        },
      });
      if (isConfirmed && rejectionReason) {
        reason = rejectionReason;
        confirmed = true;
      } else {
        toast.info("Rejection cancelled");
        return;
      }
    } else {
      const { isConfirmed } = await Swal.fire({
        title: `Update status to "${status.charAt(0).toUpperCase() + status.slice(1)
          }"?`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "Cancel",
      });
      if (isConfirmed) {
        confirmed = true;
      } else {
        toast.info("Status update cancelled");
        return;
      }
    }
    try {
      const payload = { policyId, status, agent_id };

      if (reason) payload.reason = reason;
      const response = await postData("api/policies/approve-reject", payload);
      if (response.success) {
        toast.success(response.message || "Policy updated successfully");
        fetchPolicies();
      } else {
        throw new Error(response.message || "Failed to update policy");
      }
    } catch (error) {
      toast.error(error.message || "Failed to update policy");
      console.error("Error:", error);
    }
  };

  const handleViewDetails = (policy) => {
    setSelectedPolicy(policy);
    setShowPolicyModal(true);
  };
  const handleEmailClick = (policies) => {
    setSelectedInquiry(policies);
    setShowEmailModal(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return "warning";
      case "approved":
        return "success";
      case "rejected":
        return "danger";
      default:
        return "secondary";
    }
  };

  return (
    <>
      <div className="content-page">
        <CustomLoader loading={loading} />
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="content">
          <div className="container-fluid">
            <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
              <div className="flex-grow-1">
                <h4 className="fs-18 fw-semibold m-0">Policy Management</h4>
                <p className="fw-semibold my-2">Policy List</p>
              </div>
              <div></div>
            </div>

            <div className="row">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    <form onSubmit={handleSubmit}>
                      <div className="row g-3 align-items-center">
                        <div className="col-md-12 col-lg-3">
                          <label htmlFor="searchTerm" className="form-label">
                            Search
                          </label>
                          <input
                            type="text"
                            className="form-control ps-4"
                            placeholder="Search policies..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                          />
                        </div>

                        <div className="col-md-12 col-lg-2">
                          <label htmlFor="startDate" className="form-label me-2">
                            From Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            id="startDate"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                          />
                        </div>

                        <div className="col-md-12 col-lg-2">
                          <label htmlFor="endDate" className="form-label me-2">
                            To Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            id="endDate"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                          />
                        </div>
                        <div className="col-md-12 col-lg-3 d-flex gap-2 mt-4 pt-3">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            {loading ? (
                              <span className="spinner-border spinner-border-sm me-1"></span>
                            ) : null}
                            Search
                          </button>

                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleReset}
                            disabled={loading}
                          >
                            Reset
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>

            <div className="row mt-2">
              <div className="col-12">
                <div className="card">
                  <div className="card-body">
                    <div className="table-responsive">
                      <table className="table table-striped table-hover align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>S.No</th>
                            <th>Company</th>
                            <th>Policy Number/Name</th>
                            <th>Amount</th>
                            <th>Unique ID</th>
                            <th>Agent</th>
                            <th>Customer</th>
                            <th>Status</th>
                            <th>Dates</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="10" className="text-center py-5">
                                <div
                                  className="spinner-border text-primary"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </div>
                              </td>
                            </tr>
                          ) : policies.length === 0 ? (
                            <tr>
                              <td colSpan="10" className="text-center py-4">
                                <div className="text-muted">
                                  <i className="mdi mdi-inbox-remove-outline fs-1"></i>
                                  <p className="mt-2">No policies found</p>
                                </div>
                              </td>
                            </tr>
                          ) : (
                            policies.map((policy, index) => (
                              <tr key={policy._id}>
                                <td>{currentPage * rowsPerPage + index + 1}</td>
                                <td>{policy.partnerId?.name}</td>
                                <td>
                                  <div className="fw-bold">{policy.policyNumber}</div>
                                  <small className="text-muted">{policy.insurance_plan}</small>
                                </td>
                                <td className="fw-bold">₹{policy.sale_amount}</td>
                                <td>{policy.uniqueId}</td>
                                <td>
                                  <div className="fw-bold">{policy.agent_id?.fullName}</div>
                                  <small className="text-muted">{policy.agent_id?.email}</small>
                                  <div className="text-primary">{policy.agent_id?.mobile}</div>
                                </td>
                                <td>
                                  <div className="fw-bold">{policy.user_id?.fullName}</div>
                                  <small className="text-muted">{policy.user_id?.email}</small>
                                  <div className="text-primary">{policy.user_id?.mobile}</div>
                                </td>
                                <td>
                                  <span className={`badge bg-${getStatusBadge(policy.status)}`}>
                                    {policy.status}
                                  </span>
                                </td>
                                <td>
                                  <div className="fw-bold">
                                    {new Date(policy.policyEndDate).toLocaleDateString()}
                                  </div>
                                  <small className="text-muted">
                                    {new Date(policy.policyStartDate).toLocaleDateString()}
                                  </small>
                                </td>
                                <td>
                                  <div className="dropdown">
                                    <button 
                                      className="btn btn-sm btn-outline-primary dropdown-toggle" 
                                      type="button" 
                                      data-bs-toggle="dropdown"
                                      aria-expanded="false"
                                      title="Actions"
                                    >
                                      <i className="mdi mdi-dots-horizontal"></i>
                                    </button>
                                    <ul className="dropdown-menu">
                                      <li>
                                        <button 
                                          className="dropdown-item" 
                                          onClick={() => handleViewDetails(policy)}
                                          title="View Details"
                                        >
                                          <i className="mdi mdi-eye me-2"></i> View
                                        </button>
                                      </li>
                                      <li>
                                        <button 
                                          className="dropdown-item" 
                                          onClick={() => handleEmailClick(policy?.user_id)}
                                          title="Send Email"
                                        >
                                          <i className="mdi mdi-email me-2"></i> Email
                                        </button>
                                      </li>
                                      <li>
                                        <button 
                                          className="dropdown-item" 
                                          onClick={() => {
                                            const phoneNumber = `91${policy.user_id?.mobile}`;
                                            const message = "Hello, I would like to know more!";
                                            const whatsappURL = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
                                            window.open(whatsappURL, "_blank");
                                          }}
                                          title="WhatsApp"
                                        >
                                          <i className="mdi mdi-whatsapp me-2"></i> WhatsApp
                                        </button>
                                      </li>
                                      {policy.attachmentDocument && (
                                        <li>
                                          <a 
                                            href={policy.attachmentDocument} 
                                            className="dropdown-item"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="View PDF"
                                          >
                                            <i className="mdi mdi-file-pdf me-2"></i> View PDF
                                          </a>
                                        </li>
                                      )}
                                      {policy.status === "pending" && (
                                        <>
                                          <li><hr className="dropdown-divider" /></li>
                                          <li>
                                            <button
                                              className="dropdown-item text-success"
                                              onClick={() => handleStatusUpdate(policy._id, "approved", policy.agent_id)}
                                            >
                                              <i className="mdi mdi-check me-2"></i> Approve
                                            </button>
                                          </li>
                                          <li>
                                            <button
                                              className="dropdown-item text-danger"
                                              onClick={() => handleStatusUpdate(policy._id, "rejected", policy.agent_id)}
                                            >
                                              <i className="mdi mdi-close me-2"></i> Reject
                                            </button>
                                          </li>
                                        </>
                                      )}
                                    </ul>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>

                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={<i className="mdi mdi-chevron-left"></i>}
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Email Modal */}
      <EmailModal
        showEmailModal={showEmailModal}
        setShowEmailModal={setShowEmailModal}
        selectedInquiry={selectedInquiry}
        onEmailSent={() => {
          // Handle successful email sending - could refresh data if needed
          console.log('Email sent successfully');
        }}
      />
      {/* Policy Details Modal */}
      <PolicyDetailsModal
        show={showPolicyModal}
        onHide={() => setShowPolicyModal(false)}
        policy={selectedPolicy}
      />
    </>
  );
}

export default UserPolicyListbyAgent;
